import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Heart, MessageSquare, ChevronDown, Flame, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";

const NavLink = ({ to, children, onClick, testId }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    className="text-sm font-medium text-slate-700 hover:text-[#D4A437] transition-colors duration-200"
  >
    {children}
  </Link>
);

const SERVICES = [
  { to: "/nri-services", label: "NRI Services", desc: "FEMA, repatriation, golden visa" },
  { to: "/home-loans", label: "Home Loans", desc: "24+ banks · 8.35% floor rate" },
  { to: "/interior-works", label: "Interior Works", desc: "Turnkey design · select cities" },
  { to: "/post-property", label: "Sell / Post Property", desc: "List with us" },
];

const TOOLS = [
  { to: "/insights", label: "Property Price Insights", desc: "City heatmaps · YoY growth · 2026 data", testId: "nav-tools-insights" },
  { to: "/valuation", label: "Property Valuation", desc: "Free instant fair-value estimate", testId: "nav-tools-valuation" },
  { to: "/vastu", label: "Vastu Calculator", desc: "0–100 alignment score with remedies", testId: "nav-tools-vastu" },
  { to: "/emi-calculator", label: "EMI Calculator", desc: "Plan your monthly outflow", testId: "nav-tools-emi" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useFavorites();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-sm"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img src="/oc-logo.png" alt="OC Infraventures" className="h-12 sm:h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to="/projects" testId="nav-projects">Projects</NavLink>
          <Link
            to="/hot-deals"
            data-testid="nav-hot-deals"
            className="group relative text-sm font-semibold text-[#EF4444] hover:text-[#DC2626] transition-colors duration-200 inline-flex items-center gap-1.5"
          >
            <Flame className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" fill="currentColor" />
            Hot Deals
            <span className="absolute -top-1 -right-3 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EF4444]"></span>
            </span>
          </Link>
          <NavLink to="/compare" testId="nav-compare">Compare</NavLink>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-toggle"
              className="text-sm font-medium text-slate-700 hover:text-[#D4A437] transition-colors duration-200 flex items-center gap-1"
            >
              Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div
                data-testid="nav-services-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-80"
              >
                <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden divide-y divide-slate-100">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setServicesOpen(false)}
                      data-testid={`nav-services-${s.to.slice(1)}`}
                      className="block px-5 py-3.5 hover:bg-amber-50 transition-colors group"
                    >
                      <div className="text-sm font-medium text-slate-900 group-hover:text-[#D4A437] transition-colors">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div
            className="relative"
            onMouseEnter={() => setToolsOpen(true)}
            onMouseLeave={() => setToolsOpen(false)}
          >
            <button
              data-testid="nav-tools-toggle"
              className="text-sm font-medium text-slate-700 hover:text-[#D4A437] transition-colors duration-200 flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#D4A437]" /> Tools
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
            </button>
            {toolsOpen && (
              <div
                data-testid="nav-tools-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-80"
              >
                <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden divide-y divide-slate-100">
                  {TOOLS.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setToolsOpen(false)}
                      data-testid={s.testId}
                      className="block px-5 py-3.5 hover:bg-amber-50 transition-colors group"
                    >
                      <div className="text-sm font-medium text-slate-900 group-hover:text-[#D4A437] transition-colors">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/about" testId="nav-about">About</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user && user !== false && (
            <Link to="/favorites" data-testid="nav-favorites" className="relative text-slate-600 hover:text-[#D4A437] transition-colors">
              <Heart className="w-5 h-5" strokeWidth={1.75} />
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-[#EF4444] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-bold">
                  {count}
                </span>
              )}
            </Link>
          )}
          {user && user !== false ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin/projects" data-testid="nav-admin-projects" className="text-slate-600 hover:text-[#D4A437] transition-colors text-sm font-semibold" title="Admin · Manage Projects">
                  Admin
                </Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin/inquiries" data-testid="nav-admin-inquiries" className="text-slate-600 hover:text-[#D4A437] transition-colors" title="Admin · Inquiries">
                  <MessageSquare className="w-5 h-5" strokeWidth={1.75} />
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#D4A437] transition-colors">
                <User className="w-4 h-4" strokeWidth={1.75} />
                <span>{user.name?.split(" ")[0]}</span>
              </Link>
              <button onClick={handleLogout} data-testid="nav-logout-btn" className="text-slate-500 hover:text-[#D4A437] transition-colors">
                <LogOut className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-[#D4A437] transition-colors">Login</Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="px-5 py-2.5 bg-[#D4A437] text-white text-sm font-semibold rounded-lg hover:bg-[#B88C2E] transition-colors shadow-sm"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden text-slate-700" data-testid="mobile-menu-btn">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-6 flex flex-col gap-5">
          <Link
            to="/hot-deals"
            onClick={() => setOpen(false)}
            data-testid="m-nav-hot-deals"
            className="text-sm font-semibold text-[#EF4444] inline-flex items-center gap-2"
          >
            <Flame className="w-4 h-4" fill="currentColor" /> Hot Deals
          </Link>
          <NavLink to="/projects" onClick={() => setOpen(false)} testId="m-nav-projects">Projects</NavLink>
          <NavLink to="/compare" onClick={() => setOpen(false)} testId="m-nav-compare">Compare</NavLink>
          <div className="overline pt-2">Services</div>
          <NavLink to="/nri-services" onClick={() => setOpen(false)} testId="m-nav-nri">NRI Services</NavLink>
          <NavLink to="/home-loans" onClick={() => setOpen(false)} testId="m-nav-home-loans">Home Loans</NavLink>
          <NavLink to="/interior-works" onClick={() => setOpen(false)} testId="m-nav-interior">Interior Works</NavLink>
          <NavLink to="/post-property" onClick={() => setOpen(false)} testId="m-nav-post">Post Property</NavLink>
          <div className="overline pt-2">Tools</div>
          <NavLink to="/insights" onClick={() => setOpen(false)} testId="m-nav-insights">Price Insights</NavLink>
          <NavLink to="/valuation" onClick={() => setOpen(false)} testId="m-nav-valuation">Property Valuation</NavLink>
          <NavLink to="/vastu" onClick={() => setOpen(false)} testId="m-nav-vastu">Vastu Calculator</NavLink>
          <NavLink to="/emi-calculator" onClick={() => setOpen(false)} testId="m-nav-emi">EMI Calculator</NavLink>
          <NavLink to="/about" onClick={() => setOpen(false)} testId="m-nav-about">About</NavLink>
          {user && user !== false && (
            <NavLink to="/favorites" onClick={() => setOpen(false)} testId="m-nav-favorites">Favorites ({count})</NavLink>
          )}
          {user?.role === "admin" && (
            <>
              <NavLink to="/admin/projects" onClick={() => setOpen(false)} testId="m-nav-admin-projects">Admin · Projects</NavLink>
              <NavLink to="/admin/inquiries" onClick={() => setOpen(false)} testId="m-nav-admin">Admin · Inquiries</NavLink>
            </>
          )}
          <div className="border-t border-slate-200 pt-5 flex gap-4">
            {user && user !== false ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-slate-700 font-medium" data-testid="m-nav-dashboard">Dashboard</Link>
                <button onClick={() => { handleLogout(); setOpen(false); }} className="text-slate-500 font-medium" data-testid="m-nav-logout">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="m-nav-login" className="text-slate-700 font-medium">Login</Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="m-nav-register"
                  className="px-4 py-2 bg-[#D4A437] text-white rounded-lg font-semibold"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
